import Bugsnag from '@bugsnag/js'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Formtitle, IperiaLayout, SigningFrame } from '../../components'
import IperiaNextRegistrationButton from '../../components/iperia/IperiaNextRegistrationButton'
import IperiaResignatureNotice from '../../components/iperia/IperiaResignatureNotice'
import { useIperiaStatus } from '../../hooks/useIperiaStatus'
import { client } from '../../utils/client'
import { canEditSessionStartDate, hasAmSigned, hasRejectedRequiredDocument } from '../../utils/iperiaNavigation'

// First envelope creation is slow, so use a generous timeout
const DOCUSIGN_ENVELOPE_TIMEOUT_MS = 90_000
const SIGNATURE_BLOCKED_REFRESH_MS = 10_000
const DOCUSIGN_LOAD_MAX_ATTEMPTS = 5
const DOCUSIGN_LOAD_RETRY_BASE_MS = 2000
const DOCUSIGN_LOADING_MESSAGE_INTERVAL_MS = 5000
const DOCUSIGN_LOADING_MESSAGES = [
  'Préparation de vos documents...',
  "Génération du bulletin d'inscription...",
  'Connexion à la plateforme de signature...',
  "Création de l'enveloppe électronique...",
  'Encore un instant, merci de patienter...',
]

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const Signature = () => {
  const navigate = useNavigate()
  const { global_id: globalId } = useParams<{ global_id: string }>()
  const { status, loading, error, refetch } = useIperiaStatus()

  const [docusignUrl, setDocusignUrl] = useState<string>('')
  const [docusignLoading, setDocusignLoading] = useState(true)
  const [docusignLoadFailed, setDocusignLoadFailed] = useState(false)
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [parentNotificationOpen, setParentNotificationOpen] = useState(false)
  const [awaitingSignatureConfirmation, setAwaitingSignatureConfirmation] = useState(false)
  const [externalSigningOpen, setExternalSigningOpen] = useState(false)
  const loadStartedRef = useRef(false)

  const amSigned = status ? hasAmSigned(status) : false

  const loadDocusignUrl = useCallback(async () => {
    if (!globalId) return
    loadStartedRef.current = true
    setDocusignLoading(true)
    setDocusignLoadFailed(false)

    for (let attempt = 0; attempt < DOCUSIGN_LOAD_MAX_ATTEMPTS; attempt += 1) {
      try {
        const resp = await client.get(`/registration/${globalId}/iperia/bi_signing_url/`, {
          timeout: DOCUSIGN_ENVELOPE_TIMEOUT_MS,
        })
        const data = resp.data as { signing_url?: string }
        const url = data.signing_url || ''
        if (url) {
          sessionStorage.setItem('iperia_signer', 'am')
          setDocusignUrl(url)
          setDocusignLoading(false)
          return
        }
      } catch (err) {
        if (attempt === DOCUSIGN_LOAD_MAX_ATTEMPTS - 1) {
          Bugsnag.notify(err as Error)
        }
      }
      if (attempt < DOCUSIGN_LOAD_MAX_ATTEMPTS - 1) {
        await sleep(DOCUSIGN_LOAD_RETRY_BASE_MS * (attempt + 1))
      }
    }

    loadStartedRef.current = false
    setDocusignUrl('')
    setDocusignLoadFailed(true)
    setDocusignLoading(false)
  }, [globalId])

  useEffect(() => {
    if (!status || !status.can_sign_bi || amSigned || docusignUrl || docusignLoadFailed || loadStartedRef.current) {
      return
    }
    loadDocusignUrl().catch((err: Error) => Bugsnag.notify(err))
  }, [status, amSigned, docusignUrl, docusignLoadFailed, loadDocusignUrl])

  // The sales rep generates the BI (often with the AM on the phone): refresh until it is ready.
  const blockedMessage = status?.signature_blocked_message
  useEffect(() => {
    if (!blockedMessage || amSigned) return undefined
    const intervalId = setInterval(() => {
      void refetch()
    }, SIGNATURE_BLOCKED_REFRESH_MS)
    return () => clearInterval(intervalId)
  }, [blockedMessage, amSigned, refetch])

  useEffect(() => {
    if (!docusignLoading) {
      setLoadingMessageIndex(0)
      return undefined
    }
    const intervalId = setInterval(() => {
      setLoadingMessageIndex((prev) => Math.min(prev + 1, DOCUSIGN_LOADING_MESSAGES.length - 1))
    }, DOCUSIGN_LOADING_MESSAGE_INTERVAL_MS)
    return () => clearInterval(intervalId)
  }, [docusignLoading])

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      if (event.data === 'DOCUSIGN_SIGNED_REFRESH') {
        if (!amSigned) {
          setAwaitingSignatureConfirmation(true)
        }
        refetch().catch((err: Error) => Bugsnag.notify(err))
      }
    }
    window.addEventListener('message', listener)
    return () => window.removeEventListener('message', listener)
  }, [refetch, amSigned])

  useEffect(() => {
    if (amSigned) {
      setAwaitingSignatureConfirmation(false)
      if (!status?.bi_pe_signed_at) {
        setParentNotificationOpen(true)
      }
    }
  }, [amSigned, status?.bi_pe_signed_at])

  // Keep polling while waiting for the AM signature webhook to land.
  // The iframe path starts after Edusign's success page posts back; a host that refuses
  // framing starts when the signer opens the document in a new tab.
  const watchingSignature = awaitingSignatureConfirmation || externalSigningOpen
  useEffect(() => {
    if (!watchingSignature || amSigned) return undefined
    const intervalId = setInterval(() => {
      refetch().catch((err: Error) => Bugsnag.notify(err))
    }, 3000)
    return () => clearInterval(intervalId)
  }, [watchingSignature, amSigned, refetch])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !status) {
    return (
      <Typography color="error" sx={{ p: 2 }}>
        Impossible de charger votre dossier.
      </Typography>
    )
  }

  const handleBack = () => {
    navigate(`/iperia/${globalId}/parent-employeur/adresse`)
  }

  const handleNext = () => {
    navigate(`/iperia/${globalId}/success`)
  }

  const peSigned = !!status.bi_pe_signed_at || status.workflow_status === 'pe_signed'

  // Inscription autonome dont la date ne passe plus : personne ne replanifie à sa place.
  const rescheduleButton = canEditSessionStartDate(status) && !amSigned && (
    <Button
      variant="contained"
      sx={{ alignSelf: 'flex-start' }}
      onClick={() => navigate(`/iperia/${globalId ?? ''}/verification/formation`)}
    >
      Choisir une nouvelle date
    </Button>
  )

  if (status.workflow_status === 'bi_blocked_late_start') {
    return (
      <IperiaLayout
        productName={status.learner_summary.product_name}
        handleBack={handleBack}
        handleNext={handleNext}
        submitting={false}
        submittingNext={false}
        showNext={false}
      >
        <Formtitle title="Signature du bulletin d'inscription" />
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography>
            Les formations à cheval sur les années 2026 et 2027 ne sont pas autorisées par l’APNI.
          </Typography>
          <Typography>
            De ce fait, nous vous invitons à choisir une date de formation antérieure au 10 décembre 2026 ou à partir de
            2027, afin de disposer du temps nécessaire pour réaliser votre formation dans son intégralité.
          </Typography>
          {rescheduleButton}
        </Box>
      </IperiaLayout>
    )
  }

  if (status.signature_blocked_message && !amSigned) {
    return (
      <IperiaLayout
        productName={status.learner_summary.product_name}
        handleBack={handleBack}
        handleNext={handleNext}
        submitting={false}
        submittingNext={false}
        showNext={false}
      >
        <Formtitle title="Signature du bulletin d'inscription" />
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            {/* La page se met à jour seule toutes les 10 s : on le montre, pour qu'elle ne paraisse pas figée. */}
            {!canEditSessionStartDate(status) && <CircularProgress size={18} sx={{ mt: 0.5, flexShrink: 0 }} aria-hidden />}
            <Typography>{status.signature_blocked_message}</Typography>
          </Box>
          {rescheduleButton}
        </Box>
      </IperiaLayout>
    )
  }

  const renderDocusignFallback = () => {
    if (docusignLoadFailed) {
      return (
        <Grid container direction="column" justifyContent="center" alignItems="center" sx={{ height: '100%', p: 3 }}>
          <Typography sx={{ mb: 2, textAlign: 'center' }}>
            Le chargement de vos documents prend plus de temps que prévu.
          </Typography>
          <Button variant="outlined" onClick={() => void loadDocusignUrl()}>
            Réessayer
          </Button>
        </Grid>
      )
    }
    return (
      <Grid container direction="column" justifyContent="center" alignItems="center" sx={{ height: '100%', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>{DOCUSIGN_LOADING_MESSAGES[loadingMessageIndex]}</Typography>
      </Grid>
    )
  }

  if (awaitingSignatureConfirmation && !amSigned) {
    return (
      <IperiaLayout
        productName={status.learner_summary.product_name}
        handleBack={handleBack}
        handleNext={handleNext}
        submitting={false}
        submittingNext
        showNext={false}
      >
        <Formtitle title="Signature du bulletin d'inscription" subtitle="Enregistrement de votre signature en cours…" />
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2, textAlign: 'center' }}>
            Votre signature est en cours d&apos;enregistrement. Merci de patienter.
          </Typography>
        </Box>
      </IperiaLayout>
    )
  }

  return (
    <IperiaLayout
      productName={status.learner_summary.product_name}
      handleBack={handleBack}
      handleNext={handleNext}
      submitting={false}
      submittingNext={!amSigned}
      alreadyConfirmed={amSigned}
      showNext={amSigned}
    >
      <Formtitle
        title="Signature du bulletin d'inscription"
        subtitle={
          amSigned
            ? "Vous avez signé votre bulletin d'inscription. Le parent employeur doit maintenant le signer."
            : "Signez électroniquement votre bulletin d'inscription IPERIA."
        }
      />

      {!amSigned && <IperiaResignatureNotice resignature={status.resignature} />}

      {hasRejectedRequiredDocument(status) && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate(`/iperia/${globalId ?? ''}/documents`)}>
              Remplacer le document
            </Button>
          }
        >
          Un document doit être remplacé avant que le bulletin d'inscription puisse être signé.
        </Alert>
      )}

      {peSigned && amSigned ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          Votre signature a bien été enregistrée et le bulletin d'inscription a été signé par le parent employeur.
        </Alert>
      ) : amSigned ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          Votre signature a bien été enregistrée. Le bulletin d'inscription a été envoyé au parent employeur pour
          signature.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          Après votre signature, le bulletin sera automatiquement envoyé au parent employeur pour qu'il le signe à son
          tour.
          <br />
          La date de signature est automatiquement renseignée à la signature.
        </Alert>
      )}

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
          {submitError}
        </Alert>
      )}

      {amSigned && <IperiaNextRegistrationButton />}

      {!amSigned && (
        <SigningFrame
          src={docusignUrl}
          title="Signature du bulletin d'inscription"
          fallback={renderDocusignFallback()}
          onOpened={() => setExternalSigningOpen(true)}
        />
      )}

      <Dialog open={parentNotificationOpen} onClose={() => setParentNotificationOpen(false)}>
        <DialogTitle sx={{ padding: 3 }}>Votre bulletin a bien été signé</DialogTitle>
        <DialogContent sx={{ padding: 3 }}>
          <Typography>
            Le bulletin d'inscription va être envoyé à <strong>{status.parent_employeur?.parent_email}</strong>, à vous
            de vous assurer qu'il est signé le plus rapidement possible pour finaliser votre inscription à ce parcours
            de formation.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: 3 }}>
          <Button onClick={handleNext} variant="contained">
            Continuer
          </Button>
        </DialogActions>
      </Dialog>
    </IperiaLayout>
  )
}

export default Signature
